import React, { FC, ReactNode } from 'react';
import {
  BasicContainer,
  BodyText,
  ButtonAsWrapper,
  FlexCol,
  FlexRow,
  GridDynamic,
  PaywallButton,
  TextDynamic,
} from '@minecraft.atoms';
import Icon from '@minecraft.icon';
import Modal from '@minecraft.modal';

const HEADSHOTS_INFO_MODAL_TITLE_ID = 'headshots-info-modal-title';

export type HeadshotsInfoBenefit = {
  id: string;
  label: ReactNode;
};

export type HeadshotsInfoModalProps = {
  title: string;
  body: ReactNode;
  benefits: HeadshotsInfoBenefit[];
  prompt: string;
  primaryActionLabel: string;
  onPrimaryAction: () => void;
  secondaryActionLabel: string;
  onSecondaryAction: () => void;
  closeLabel: string;
  onClose: () => void;
};

/**
 * Shared headshots upsell / info modal. Copy and actions are props so trial,
 * free, monthly, and annual variants can reuse the same layout.
 */
export const HeadshotsInfoModal: FC<HeadshotsInfoModalProps> = ({
  title,
  body,
  benefits,
  prompt,
  primaryActionLabel,
  onPrimaryAction,
  secondaryActionLabel,
  onSecondaryAction,
  closeLabel,
  onClose,
}) => {
  return (
    <Modal
      isOpen
      isHeaderHidden
      isFooterHidden
      isCloseHidden
      noPadding
      height="auto"
      modalSize="medium"
      labelledBy={HEADSHOTS_INFO_MODAL_TITLE_ID}
      handleToggleClick={onClose}
      data-testid="headshots-info-modal"
    >
      <GridDynamic
        className="cn_relative cn_overflow-hidden"
        w="full"
        columns="1fr"
        bgColor="card"
        borderRadius="lg"
        alignItems="stretch"
        mdProps={{ columns: '1fr 1fr' }}
      >
        <FlexCol
          w="full"
          h="52"
          grow={0}
          alignItems="center"
          justifyContent="center"
          bgColor="cardInverted"
          mdProps={{ h: 'full' }}
        >
          {/* Gallery images land here once the headshots carousel assets are available. */}
        </FlexCol>
        <FlexCol
          w="full"
          grow={0}
          padding="6"
          className="cn_box-sizing_border-box"
          mdProps={{
            padding: undefined,
            className: 'cn_box-sizing_border-box cn_atom_px-8 cn_atom_pt-8 cn_atom_pb-8',
          }}
        >
          <BasicContainer h="8" className="cn_hidden cn_md:block" />
          <BasicContainer h="4" className="cn_hidden cn_md:block" />
          <FlexCol gap="8" w="full" grow={0}>
            <FlexCol gap="2" w="full" grow={0}>
              <TextDynamic
                id={HEADSHOTS_INFO_MODAL_TITLE_ID}
                as="h2"
                fontWeight="medium"
                fontSize="h3"
                mdProps={{ fontSize: 'h2', fontWeight: 'medium' }}
              >
                {title}
              </TextDynamic>
              <BodyText>{body}</BodyText>
            </FlexCol>
            <FlexCol gap="2" w="full" grow={0}>
              {benefits.map((benefit) => (
                <FlexRow key={benefit.id} gap="2" alignItems="center" wrap="nowrap">
                  <Icon name="checkmark" size="md" themeColor="success" />
                  <TextDynamic fontSize="bodyRegular" mdProps={{ fontSize: 'bodyLarge' }}>
                    {benefit.label}
                  </TextDynamic>
                </FlexRow>
              ))}
            </FlexCol>
            <BodyText>{prompt}</BodyText>
          </FlexCol>
          <BasicContainer h="8" className="cn_md:hidden" />
          <BasicContainer h="10" className="cn_hidden cn_md:block" />
          <BasicContainer h="8" className="cn_hidden cn_md:block" />
          <FlexCol
            gap="2"
            w="full"
            grow={0}
            alignItems="center"
            className="cn_box-sizing_border-box"
            mdProps={{ className: 'cn_box-sizing_border-box cn_atom_px-3' }}
          >
            <PaywallButton
              color="gold"
              label={primaryActionLabel}
              full
              block
              borderRadius="sm"
              className="cn_box-sizing_border-box"
              mdProps={{ full: true, block: true }}
              lgProps={{ full: true, block: true }}
              onClick={onPrimaryAction}
              data-testid="headshots-info-modal-primary-action"
            />
            <ButtonAsWrapper
              aria-label={secondaryActionLabel}
              onClick={onSecondaryAction}
              fullWidth
              noPadding
              data-testid="headshots-info-modal-secondary-action"
            >
              <TextDynamic
                as="span"
                fontWeight="medium"
                align="center"
                fontSize="bodyRegular"
                mdProps={{ fontSize: 'bodyLarge', fontWeight: 'medium' }}
              >
                {secondaryActionLabel}
              </TextDynamic>
            </ButtonAsWrapper>
          </FlexCol>
        </FlexCol>
        <BasicContainer className="cn_absolute" top={1} right={1} zIndex={2}>
          <ButtonAsWrapper aria-label={closeLabel} onClick={onClose} noPadding data-testid="headshots-info-modal-close">
            <BasicContainer className="cn_md:hidden">
              <Icon name="close" size="sm" themeColor="inverted" />
            </BasicContainer>
            <BasicContainer className="cn_hidden cn_md:block">
              <Icon name="close" size="sm" themeColor="active" />
            </BasicContainer>
          </ButtonAsWrapper>
        </BasicContainer>
      </GridDynamic>
    </Modal>
  );
};

export default HeadshotsInfoModal;
